import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';

const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('4.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
   navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '4.1-1': 1, // Symptoms may develop hours or days later and can worsen if untreated
    '4.1-2': 1, // Bluish skin discoloration (cyanosis)
    '4.1-3': 1, // Every 15 minutes
    '4.2-1': 1, // Patients with dysphagia or swallowing difficulties
    '4.2-2': 1, // To assess the patient's ability to resume a safe oral diet
    '4.2-3': 1, // Thickened fluids and pureed foods
    '4.3-1': 2, // They may experience anxiety around eating or fear of choking again
    '4.3-2': 1, // Provide reassurance and clear information about safety measures
    '4.3-3': 0, // Cognitive-behavioral techniques
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "4.1",
      title: "4.1 Observation Post-Resolution",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TransferWithinAStationIcon sx={{ mr: 1, color: "#81c784" }} />
            4.1 Observation Post-Resolution
          </Typography>

          <Typography paragraph>
            Even after successful intervention, patients require ongoing assessment to ensure their airway remains clear and no delayed complications arise.
          </Typography>

          <Box
            sx={{
              bgcolor: "rgba(129, 199, 132, 0.1)",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Key Monitoring Areas
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Aspiration Pneumonia
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Persistent cough" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Chest pain" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Fever" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Difficulty breathing" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Hypoxia Signs
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Confusion or disorientation" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Bluish skin discoloration" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Rapid breathing or heart rate" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Airway Trauma
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Hoarseness" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Difficulty swallowing" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Throat pain or discomfort" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Vital Sign Monitoring Schedule
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    First Hour
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AccessibilityNewIcon sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Check every 15 minutes"
                        secondary="Ensures early detection of complications"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessibilityNewIcon sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Monitor respiratory status"
                        secondary="Watch for signs of distress"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Next Two Hours
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AccessibilityNewIcon sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Check every 30 minutes"
                        secondary="As patient stabilizes"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessibilityNewIcon sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Continue respiratory assessment"
                        secondary="Monitor for delayed complications"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 4.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is monitoring for aspiration pneumonia important after a choking episode?
                    </Typography>
                    <List dense>
                      {[
                        "It helps detect signs of internal bleeding",
                        "Symptoms may develop hours or days later and can worsen if untreated",
                        "It confirms that the patient is no longer choking",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.1-1"] === index) {
                          if (index === correctAnswers["4.1-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.1-1"] &&
                          index === correctAnswers["4.1-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.1", "1", index)
                            }
                            disabled={
                              answerStatus["4.1-1"] === true &&
                              index !== correctAnswers["4.1-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.1-1"] === true &&
                                index !== correctAnswers["4.1-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.1-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Aspiration pneumonia may have a delayed onset, making continuous monitoring essential for early detection. Symptoms can develop hours or days after the choking incident and can worsen if left untreated.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is a key sign of hypoxia that requires immediate attention?
                    </Typography>
                    <List dense>
                      {[
                        "Increased appetite",
                        "Bluish skin discoloration (cyanosis)",
                        "Sweating",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.1-2"] === index) {
                          if (index === correctAnswers["4.1-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.1-2"] &&
                          index === correctAnswers["4.1-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.1", "2", index)
                            }
                            disabled={
                              answerStatus["4.1-2"] === true &&
                              index !== correctAnswers["4.1-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.1-2"] === true &&
                                index !== correctAnswers["4.1-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.1-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Cyanosis (bluish skin discoloration) indicates low oxygen levels in the blood, which can be life-threatening without prompt intervention. This is a critical sign that requires immediate medical attention.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      How often should vital signs be checked during the first hour after a choking incident?
                    </Typography>
                    <List dense>
                      {[
                        "Every 30 minutes",
                        "Every 15 minutes",
                        "Every hour",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.1-3"] === index) {
                          if (index === correctAnswers["4.1-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.1-3"] &&
                          index === correctAnswers["4.1-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.1", "3", index)
                            }
                            disabled={
                              answerStatus["4.1-3"] === true &&
                              index !== correctAnswers["4.1-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.1-3"] === true &&
                                index !== correctAnswers["4.1-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.1-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Vital signs should be checked every 15 minutes during the first hour after a choking incident to ensure early detection of any complications and allow for prompt intervention if needed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "4.2",
      title: "4.2 Precautions for High-Risk Patients",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} /> 4.2
            Precautions for High-Risk Patients
          </Typography>

          <Typography paragraph>
            Certain patients are more vulnerable to complications following a choking episode. These individuals require additional monitoring and tailored interventions to ensure their recovery.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              High-Risk Patient Groups
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Neuromuscular Disorders
                    </Typography>
                    <Typography variant="body2">
                      Patients with conditions like Parkinson's disease or multiple sclerosis may have impaired swallowing and increased choking risk.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Dysphagia
                    </Typography>
                    <Typography variant="body2">
                      Patients with swallowing difficulties may struggle to manage solid foods, liquids, or even saliva.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Sedation/Impaired Consciousness
                    </Typography>
                    <Typography variant="body2">
                      Medications causing drowsiness may suppress the patient's ability to protect their airway.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Speech and Swallowing Assessment
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Professional evaluation"
                        secondary="Observation while swallowing liquids or solids"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Texture modifications"
                        secondary="Recommendations for thickened fluids, soft foods"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Swallowing therapy"
                        secondary="Improve muscle control and techniques"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Dietary Modifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Pureed or minced diets"
                        secondary="Reduce choking hazards"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Thickened fluids"
                        secondary="Easier to control and swallow"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Small, frequent meals"
                        secondary="Reduce fatigue during eating"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 4.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which patient group is most at risk of delayed complications after choking?
                    </Typography>
                    <List dense>
                      {[
                        "Patients with mild cough symptoms",
                        "Patients with dysphagia or swallowing difficulties",
                        "Patients who are physically active",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.2-1"] === index) {
                          if (index === correctAnswers["4.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.2-1"] &&
                          index === correctAnswers["4.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.2", "1", index)
                            }
                            disabled={
                              answerStatus["4.2-1"] === true &&
                              index !== correctAnswers["4.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.2-1"] === true &&
                                index !== correctAnswers["4.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Patients with dysphagia or swallowing difficulties are at highest risk of delayed complications after choking because their underlying condition makes them more susceptible to aspiration and other respiratory complications.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is a swallowing assessment recommended for high-risk patients after a choking episode?
                    </Typography>
                    <List dense>
                      {[
                        "To evaluate the patient's hydration levels",
                        "To assess the patient's ability to resume a safe oral diet",
                        "To determine if the patient can stand without assistance",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.2-2"] === index) {
                          if (index === correctAnswers["4.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.2-2"] &&
                          index === correctAnswers["4.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.2", "2", index)
                            }
                            disabled={
                              answerStatus["4.2-2"] === true &&
                              index !== correctAnswers["4.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.2-2"] === true &&
                                index !== correctAnswers["4.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A swallowing assessment is crucial to evaluate the patient's ability to safely resume eating and drinking, identifying any issues that could increase the risk of future choking incidents.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which dietary change is most appropriate for a patient with swallowing difficulties post-choking?
                    </Typography>
                    <List dense>
                      {[
                        "Raw vegetables and hard foods",
                        "Thickened fluids and pureed foods",
                        "Carbonated drinks and spicy foods",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.2-3"] === index) {
                          if (index === correctAnswers["4.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.2-3"] &&
                          index === correctAnswers["4.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.2", "3", index)
                            }
                            disabled={
                              answerStatus["4.2-3"] === true &&
                              index !== correctAnswers["4.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.2-3"] === true &&
                                index !== correctAnswers["4.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Thickened fluids and pureed foods are the most appropriate dietary changes for patients with swallowing difficulties as they are easier to control and swallow, reducing the risk of choking and aspiration.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "4.3",
      title: "4.3 Psychological Support and Follow-up",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChatIcon sx={{ mr: 1, color: "#81c784" }} />
            4.3 Psychological Support and Follow-up
          </Typography>

          <Typography paragraph>
            Choking incidents can be traumatic for patients, potentially causing significant psychological distress. Addressing these emotional impacts is an essential component of comprehensive post-choking care, as psychological well-being directly affects recovery and future health behaviors.
          </Typography>

          {/* Psychological Impact Visualization */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Common Psychological Responses to Choking Incidents
            </Typography>
            
            <Box sx={{ mt: 3, position: "relative" }}>
              {/* This would be replaced with an actual chart component */}
              <Box sx={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap" }}>
                <Box sx={{ textAlign: "center", m: 2, position: "relative" }}>
                  <Box sx={{ width: 160, height: 160, borderRadius: "50%", border: "8px solid #81c784", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32" }}>78%</Typography>
                    <Typography variant="body2" sx={{ px: 2 }}>Anxiety during meals</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ textAlign: "center", m: 2, position: "relative" }}>
                  <Box sx={{ width: 140, height: 140, borderRadius: "50%", border: "8px solid #a5d6a7", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32" }}>65%</Typography>
                    <Typography variant="body2" sx={{ px: 2 }}>Fear of recurrence</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ textAlign: "center", m: 2, position: "relative" }}>
                  <Box sx={{ width: 120, height: 120, borderRadius: "50%", border: "8px solid #c8e6c9", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32" }}>42%</Typography>
                    <Typography variant="body2" sx={{ px: 2 }}>Eating avoidance</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ textAlign: "center", m: 2, position: "relative" }}>
                  <Box sx={{ width: 100, height: 100, borderRadius: "50%", border: "8px solid #e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32" }}>30%</Typography>
                    <Typography variant="body2" sx={{ px: 2 }}>PTSD symptoms</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", fontStyle: "italic" }}>
              Based on survey data of patients following severe choking incidents
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Immediate Psychological Support
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Validation and Reassurance" 
                        secondary="Acknowledge the frightening nature of the experience and provide empathetic support" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Clear Information" 
                        secondary="Explain what happened, what was done, and what to expect during recovery"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Stress-Reduction Techniques" 
                        secondary="Teach simple breathing exercises and grounding techniques to manage acute anxiety"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Family Involvement" 
                        secondary="Include family members in support and education efforts to reinforce safety"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Long-term Support Strategies
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Referral to Mental Health Services" 
                        secondary="Connect patients experiencing significant distress with appropriate psychological support"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Support Groups" 
                        secondary="Provide information about peer support options for sharing experiences"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Gradual Exposure" 
                        secondary="Work with patients to gradually reintroduce foods or eating situations that cause anxiety"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Follow-up Appointments" 
                        secondary="Schedule regular check-ins to assess psychological recovery alongside physical recovery"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ my: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Therapeutic Approaches for Post-Choking Anxiety
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Cognitive-Behavioral Therapy
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Identifying anxiety triggers" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Challenging negative thoughts" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Developing coping strategies" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Behavior modification techniques" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Mindfulness Practices
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Mindful eating exercises" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Present-moment awareness" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Breathing techniques" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Body scan meditations" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Educational Approaches
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Knowledge about safe eating" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Understanding normal swallowing" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Self-rescue techniques" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Prevention strategies" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 4.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is psychological support important for patients after a choking incident?
                    </Typography>
                    <List dense>
                      {[
                        "It is required by healthcare regulations",
                        "It reduces the facility's legal liability",
                        "They may experience anxiety around eating or fear of choking again",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.3-1"] === index) {
                          if (index === correctAnswers["4.3-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.3-1"] &&
                          index === correctAnswers["4.3-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.3", "1", index)
                            }
                            disabled={
                              answerStatus["4.3-1"] === true &&
                              index !== correctAnswers["4.3-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.3-1"] === true &&
                                index !== correctAnswers["4.3-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Choking incidents can cause significant psychological trauma that may lead to anxiety, food avoidance, or even PTSD-like symptoms, which can interfere with nutrition and quality of life if not addressed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the most appropriate immediate response to a patient showing anxiety after a choking incident?
                    </Typography>
                    <List dense>
                      {[
                        "Recommend medication to reduce anxiety symptoms",
                        "Provide reassurance and clear information about safety measures",
                        "Tell them their anxiety is unnecessary since the choking has been resolved",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.3-2"] === index) {
                          if (index === correctAnswers["4.3-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.3-2"] &&
                          index === correctAnswers["4.3-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.3", "2", index)
                            }
                            disabled={
                              answerStatus["4.3-2"] === true &&
                              index !== correctAnswers["4.3-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.3-2"] === true &&
                                index !== correctAnswers["4.3-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.3-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Providing empathetic reassurance and clear, factual information helps address immediate anxiety while giving patients a sense of control and understanding about their situation.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which therapeutic approach focuses on identifying and modifying negative thought patterns about eating after a choking incident?
                    </Typography>
                    <List dense>
                      {[
                        "Cognitive-behavioral techniques",
                        "Physical rehabilitation exercises",
                        "Pharmaceutical interventions",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["4.3-3"] === index) {
                          if (index === correctAnswers["4.3-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["4.3-3"] &&
                          index === correctAnswers["4.3-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("4.3", "3", index)
                            }
                            disabled={
                              answerStatus["4.3-3"] === true &&
                              index !== correctAnswers["4.3-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["4.3-3"] === true &&
                                index !== correctAnswers["4.3-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["4.3-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Cognitive-behavioral techniques help patients identify catastrophic thoughts about eating, challenge those thoughts with evidence, and gradually develop more adaptive perspectives on food safety.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 4: Post-Choking Monitoring and Reassessment
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Learn to monitor for complications post-resolution, assess respiratory
          status, and ensure proper follow-up care.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
